// THE BEAM CHARGE + THE FLIGHT'S FILL (PC; APP_STARTUP §7; owner 2026-10-31
// "something semi-manual is best", "bridge the time taking gaps with some …
// informative … displays"). One beam language: CHARGE → FLIGHT → LANDING.
//   charge  the card's ⤳ Beam is pressed or CLICKED (inspect.js; main.js
//           beamCharge; owner 2026-10-31 "it must be super intuitive"): the
//           landing preloads from the press (the ground tree's leadEye + the
//           painter's landing view; before entry the card's prefetch), a light
//           column stands at the target, the button fills over charge_ms and
//           the beam launches itself when full (held or not: an early release
//           never cancels); Esc or a second click on the button = nothing. The column's intensity is the landing view's readiness
//           (readiness: the flight's own test — never a timer) and flickers
//           while the flight waits on it (the pace's bound).
//   fill    in the flight: the names the 3D view would label (the gazetteer's
//           own inView: scored, line-of-sight tested) of the kinds in
//           LABEL_KINDS, at most label_cap, fading; an arrival card (the
//           place, the landing altitude, a lake's live water temperature)
//           in the higher part (s < card_s). Only data the world ships.
// Pure parts here (check-mapfirst §5c); main.js wires them.
import * as THREE from 'three';

export const LABEL_KINDS = new Set(['peak', 'lake', 'city', 'town', 'village', 'hamlet', 'place', 'suburb']);
const PLACE_KINDS = ['city', 'town', 'village', 'hamlet', 'place', 'suburb'];

// the charge's fill 0..1 (t0: the press) and what an event does to it: ev
// 'tick' | 'release' (the button let go) → 'launch' when full, else 'wait';
// 'esc' | 'again' (a second click on the button) → 'cancel'
export function chargeFill(t0, now, F) { return Math.max(0, Math.min(1, (now - t0) / F.charge_ms)); }
export function chargeAct(fill, ev) {
  if (ev === 'esc' || ev === 'again') return 'cancel';
  return fill >= 1 ? 'launch' : 'wait';
}
// a readiness test (beamflight.js flightTest) → 0..1: the share of the view's
// ground rays already drawn at the texel the pose needs; 1 when it passes
export function readiness(r) {
  if (!r) return 0;
  if (r.ok) return 1;
  if (!r.rays) return 0;
  return (Math.max(0, r.rays - r.white - r.tex) / r.rays) * (r.geo || r.bld ? 0.9 : 1);
}
// the column's opacity: its readiness; flickering while the flight is held on it
export function columnAlpha(ready, held, now) {
  const a = 0.2 + 0.7 * Math.max(0, Math.min(1, ready));
  return held ? a * (0.5 + 0.5 * Math.abs(Math.sin(now / 70))) : a;
}
// the flight's labels: the 3D view's own (gazetteer.inView, in its rank
// order) of LABEL_KINDS with a world anchor, at most label_cap
export function flightLabels(features, F) {
  const out = [];
  for (const f of features || []) {
    if (!LABEL_KINDS.has(f.kind) || f.wx === undefined || !f.name) continue;
    out.push(f);
    if (out.length >= F.label_cap) break;
  }
  return out;
}
// the arrival card: o { label, x, y, z, gaz, water, now } → { name, alt, lake:
// { name, c } | null }. name: the Beam's label, else the nearest place within
// 3 km (gazetteer); alt: the landing ground (m a.s.l.); lake: the nearest lake
// within card_lake_m whose live temperature (water.json lake.live → the game
// child's /live/water.json) is known
export function arrivalCard(o, F) {
  let name = o.label || '';
  if (!name && o.gaz) { const p = o.gaz.within(o.x, o.z, 3000, PLACE_KINDS)[0]; if (p) name = p.name; }
  let lake = null, best = F.card_lake_m;
  for (const l of (o.water && o.water.lakes) || []) {
    const b = l.bbox;
    if (!b) continue;
    const d = Math.hypot(Math.max(b[0] - o.x, 0, o.x - b[2]), Math.max(b[1] - o.z, 0, o.z - b[3]));
    if (d > best) continue;
    const tp = o.water.tempOfLake ? o.water.tempOfLake(l, o.now) : null;
    if (!tp || !Number.isFinite(tp.c)) continue;
    best = d; lake = { name: l.name || '', c: tp.c };
  }
  return { name, alt: Number.isFinite(o.y) ? Math.round(o.y) : null, lake };
}

// THE COLUMN: a light column + a ground ring at the target (additive, no
// depth write, never a collider or a ray target); its width keeps a few
// pixels at any range (scaled by the camera distance each frame)
export class BeamColumn {
  constructor(scene, F) {
    const mat = (o) => new THREE.MeshBasicMaterial({ color: 0x67e8f9, transparent: true, opacity: o, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    this.g = new THREE.Group();
    this.col = new THREE.Mesh(new THREE.CylinderGeometry(1, 1, F.col_h_m, 12, 1, true), mat(0.5));
    this.col.position.y = F.col_h_m / 2;
    this.ring = new THREE.Mesh(new THREE.RingGeometry(1.6, 2, 32), mat(0.6));
    this.ring.rotation.x = -Math.PI / 2;
    this.ring.position.y = 0.3;
    this.g.add(this.col, this.ring);
    this.g.renderOrder = 10;
    this.g.userData.noRaycast = true;
    this.g.raycast = () => {};
    this.col.raycast = () => {};
    this.ring.raycast = () => {};
    this.scene = scene;
    scene.add(this.g);
  }
  at(x, y, z) { this.g.position.set(x, y, z); }
  update(cam, alpha) {
    const d = cam.position.distanceTo(this.g.position);
    const r = Math.max(1.5, d * 0.003);
    this.col.scale.set(r, 1, r);
    this.ring.scale.set(r * 2, r * 2, 1);
    this.col.material.opacity = alpha;
    this.ring.material.opacity = Math.min(1, alpha * 1.3);
  }
  dispose() {
    this.scene.remove(this.g);
    for (const m of [this.col, this.ring]) { m.geometry.dispose(); m.material.dispose(); }
  }
}

// THE LABEL LAYER (#beam-labels: label_cap pooled divs) + the card (#beam-card)
export class FlightFill {
  constructor(F) {
    this.F = F;
    this.el = document.getElementById('beam-labels');
    this.card = document.getElementById('beam-card');
    this.pool = [];
    this.on = [];
    this.at = -Infinity;
    if (this.el) {
      for (let i = 0; i < Math.max(F.label_cap, F.wait_names || 0); i++) {
        const d = document.createElement('div');
        d.className = 'bl';
        this.el.appendChild(d);
        this.pool.push(d);
      }
    }
  }
  // query(): the view's features (gazetteer.inView) — at most every label_ms
  labels(cam, now, query, show) {
    if (!this.el) return;
    this.el.hidden = !show;
    if (!show) return;
    if (now - this.at >= this.F.label_ms) { this.at = now; this.on = flightLabels(query(), this.F); }
    const w = innerWidth, h = innerHeight, v = new THREE.Vector3();
    for (let i = 0; i < this.pool.length; i++) {
      const d = this.pool[i], f = this.on[i];
      if (!f) { d.classList.remove('on'); continue; }
      v.set(f.wx, f.wy, f.wz).project(cam);
      if (v.z > 1 || Math.abs(v.x) > 1 || Math.abs(v.y) > 1) { d.classList.remove('on'); continue; }
      const txt = f.kind === 'peak' && f.ele ? `${f.name} · ${Math.round(f.ele)} m` : f.name;
      if (d.textContent !== txt) d.textContent = txt;
      d.style.transform = `translate(${((v.x + 1) / 2 * w).toFixed(1)}px, ${((1 - v.y) / 2 * h).toFixed(1)}px) translate(-50%, -100%)`;
      d.classList.add('on');
    }
  }
  // the Map wait's names (beamlabels.js waitNames: { txt, x, z }), the first n in,
  // over the 2D stage's view v (mpp per device px; px = device px per CSS px)
  mapNames(list, n, v, px) {
    if (!this.el) return;
    this.el.hidden = false;
    const w = innerWidth, h = innerHeight, c = Math.cos(v.rot || 0), s = Math.sin(v.rot || 0), m = v.mpp * px;
    for (let i = 0; i < this.pool.length; i++) {
      const d = this.pool[i], f = i < n ? list[i] : null;
      if (!f) { d.classList.remove('on'); continue; }
      const dx = (f.x - v.cx) / m, dz = (f.z - v.cz) / m, sx = w / 2 + dx * c + dz * s, sy = h / 2 - dx * s + dz * c;
      if (sx < 8 || sy < 24 || sx > w - 8 || sy > h - 8) { d.classList.remove('on'); continue; }
      if (d.textContent !== f.txt) d.textContent = f.txt;
      d.style.transform = `translate(${sx.toFixed(1)}px, ${sy.toFixed(1)}px) translate(-50%, -100%)`;
      d.classList.add('on');
    }
  }
  // c: arrivalCard() | null (hidden); fmt(c) → the card's lines
  showCard(c, lines) {
    if (!this.card) return;
    this.card.hidden = !c;
    if (!c) return;
    const html = lines.map((s, i) => `<div class="${i ? 'bc-sub' : 'bc-name'}"></div>`).join('');
    if (this.card.childElementCount !== lines.length) this.card.innerHTML = html;
    lines.forEach((s, i) => { const n = this.card.children[i]; if (n.textContent !== s) n.textContent = s; });
  }
  stop() {
    if (this.el) { this.el.hidden = true; for (const d of this.pool) d.classList.remove('on'); }
    if (this.card) this.card.hidden = true;
  }
}
